import React, { FC, useContext, useMemo } from 'react';

import BasicButton from '../../../components/buttons/basicButton';

import { FormulaType } from '../../../constants/formulaType';

import { EditorContext } from '../../../context/EditorContext';

import {
    getSectionVariantType,
    generateCsvContent,
    groupVariantAlternatesWithLabels,
    transformHeaderData,
} from './helpers';

import useComponentTypesQuery from '../../../hooks/component-types/useComponentTypesQuery';
import useVariantsByMessageContentIdQuery from 'hooks/variants/useVariantsByMessageContentIdQuery';

const FormulaAlternateExport: FC = () => {
    const {
        editorDetails: { formula, formulaType },
    } = useContext(EditorContext);

    const { data: componentTypes } = useComponentTypesQuery();

    const headerData = useMemo(() => {
        if (formula) {
            return transformHeaderData(
                formula?.structure!,
                formulaType as FormulaType,
                componentTypes?.map ?? {},
            );
        }
    }, [formula, formulaType, componentTypes]);

    const sectionTypeOrderMap = useMemo<Record<string, string[]>>(() => {
        const sectionTypeOrderMap: Record<string, string[]> = {};
        headerData?.headers.forEach((section) => {
            sectionTypeOrderMap[
                section.headerName.charAt(0).toUpperCase() +
                    section.headerName.slice(1)
            ] = section.children.map(
                (child) =>
                    `${componentTypes?.map?.[child.variantType as any]?.name}_${
                        child.id
                    }` || '',
            );
        });
        return sectionTypeOrderMap;
    }, [headerData]);

    const { data } = useVariantsByMessageContentIdQuery(formula?.id as number);

    const handleExport = () => {
        if (!headerData?.headers || !data) return;

        const variantMap = getSectionVariantType(
            headerData.headers,
            componentTypes?.map!,
        );

        const grouped = groupVariantAlternatesWithLabels(data, variantMap);
        const csvContent = generateCsvContent(
            grouped,
            headerData.sectionOrder,
            sectionTypeOrderMap,
        );

        const blob = new Blob([csvContent], {
            type: 'text/csv;charset=utf-8;',
        });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.setAttribute('download', `${formula?.id}_components_export.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <BasicButton
            name="Export as CSV"
            action={handleExport}
            isPrimary={false}
            isDisabled={!data?.length}
        />
    );
};

export default FormulaAlternateExport;
